/*
A composable that provides access to client features configuration objects from context store.
Note that context.clientConfig will be deprecated in the future in favor of context.features.
*/
import { get, isArray, size } from 'lodash';
import SIZE_TYPES from '@enums/size-types';
import { computed } from 'vue';
// TODO: aov3-8283 fix circular dependency issue
// eslint-disable-next-line import/no-cycle
import { useContextStore } from '@/stores/context';
import { isEmbedded } from '@/webshell-embed/embedded';

console.log(
  'useFeature: context.clientConfig will be deprecated in the future, use context.features instead'
);

export function useFeature() {
  const context = useContextStore();

  // AOV3-5809 this will be DEPRECATED in the future in favor of context.features
  const clientConfig = context.clientConfig;
  const features = context.features;

  // =============================================
  // Bundles and Templates
  // =============================================

  const bundlesEnabled = computed(() => {
    return get(clientConfig, 'features.bundlesEnabled', false);
  });

  const templatesEnabled = computed(() => {
    return get(clientConfig, 'features.templatesEnabled', false);
  });

  const hasProductsExistingFromNewInTemplateEnabled = computed(() => {
    return get(clientConfig, 'features.productsExistingFromNewInTemplateEnabled', false);
  });

  // ============================================
  // Workpackages
  // ============================================

  const freshEnabled = computed(() => {
    return get(features, 'workpackages.freshEnabled', false);
  });

  const gridContextMenusEnabled = computed(() => {
    return get(features, 'workpackages.gridContextMenusEnabled', false);
  });

  const hideAnalysisOnlySelection = computed(() => {
    return get(features, 'workpackages.hideAnalysisOnlySelection', false);
  });

  const historicKPIsEnabled = computed(() => {
    return get(features, 'workpackages.historicKPIsEnabled', false);
  });

  const performancePeriodPreSetSelectMenuEnabled = computed(() => {
    return get(features, 'workpackages.performancePeriodPreSetSelectMenu', false);
  });

  const performancePeriodPreSetSelectOptions = computed(() => {
    return get(features, 'workpackages.performancePeriodPreSetSelectOptions', []);
  });

  const previousWorkpackageLinkingEnabled = computed(() => {
    return get(features, 'workpackages.previousWorkpackageLinking', false);
  });

  const setByEndDateDefault = computed(() => {
    return get(features, 'workpackages.setByEndDateDefault', false);
  });

  const showSetByEndDateToggle = computed(() => {
    return get(features, 'workpackages.showSetByEndDateToggle', false);
  });

  const syntheticFurnitureEnabled = computed(() => {
    return get(features, 'workpackages.syntheticFurnitureEnabled', false);
  });

  const defaultFillAccordingTo = computed(() => {
    return get(features, 'workpackages.defaultFillAccordingTo', SIZE_TYPES.linearSpace);
  });

  // =============================================
  // Products
  // =============================================

  const hasProductLineEnabled = computed(() => {
    return get(clientConfig, 'features.productLineEnabled', false);
  });

  const hasKPKUEnabled = computed(() => {
    return get(clientConfig, 'features.kpkuEnabled', false);
  });

  const hasPalletContentEnabled = computed(() => {
    return get(clientConfig, 'features.palletContentEnabled', false);
  });

  const hasClientProductKeyColumnEnabled = computed(() => {
    return get(clientConfig, 'features.scenario.products.displayOriginalClientKey', false);
  });

  const autoReAddRecentlySoldProductsWhenNotChild = computed(() => {
    return get(clientConfig, 'features.autoReAddRecentlySoldProductsWhenNotChild', false);
  });

  const dependentProductsEnabled = computed(() => {
    return get(clientConfig, 'features.scenario.products.dependentProductsEnabled', false);
  });

  // =============================================
  // Product Modelling
  // =============================================

  const isProductModellingToggleProductViewsEnabled = computed(() => {
    return get(features, 'modelling.isViewToggleEnabled', false);
  });

  const isCopyModellingDecisionsEnabled = computed(() => {
    return (
      get(
        clientConfig,
        'features.scenario.productModelling.copyModellingDecisionsEnabled',
        false
      ) || // for backward compatibility
      get(features, 'modelling.isCopyModellingDecisionsEnabled', false)
    );
  });

  const isTotalSalesOverrideEnabled = computed(() => {
    return (
      get(clientConfig, 'features.scenario.productModelling.totalSalesOverrideEnabled', false) || // for backward compatibility
      get(features, 'modelling.isTotalSalesOverrideEnabled', false)
    );
  });

  const isVolumeRoSEnabled = computed(() => {
    return get(features, 'modelling.isVolumeRoSEnabled', false);
  });

  const useMarkdownAndWasteToCalculateMarginAndPriceEnabled = computed(() => {
    return get(features, 'modelling.useMarkdownAndWasteToCalculateMarginAndPrice', false);
  });

  // TODO move to use-configuration.js new composable, as it's client configuration, not feature toggle
  const productModellingAlertsCriteria = computed(() => {
    return get(clientConfig, 'productModellingAlertsCriteria', {});
  });

  // TODO move to use-configuration.js new composable, as it's client configuration, not feature toggle
  const productModellingViewsToggleType = computed(() => {
    // allows to view the subset of products that are new / non-established
    return get(clientConfig, 'features.scenario.productModelling.viewsToggle.type', 'new');
  });

  const modellingMainPageAllowedColumns = computed(() => {
    const value = get(
      clientConfig,
      'features.scenario.productModelling.modellingMainPageAllowedColumns',
      []
    );

    return isArray(value) && size(value) ? value : [];
  });

  // TODO move to use-configuration.js new composable, as it's client configuration, not feature toggle
  const showProductModellingForNewProductsOnly = computed(() => {
    return get(clientConfig, 'features.scenario.productModelling.showNewProductsOnly', true);
  });

  // TODO move to use-configuration.js new composable, as it's client configuration, not feature toggle
  const modellingDetailPageAllowedColumns = computed(() => {
    const value = get(
      clientConfig,
      'features.scenario.productModelling.modellingDetailPageAllowedColumns',
      []
    );

    return isArray(value) && size(value) ? value : [];
  });

  const defaultModellingViewToAllProducts = computed(() => {
    return get(features, 'modelling.defaultModellingViewToAllProducts', false);
  });

  const hideWasteAndMarkdownColumns = computed(() => {
    return get(features, 'modelling.hideWasteAndMarkdownColumns', false);
  });

  // =============================================
  // Assortment Canvas
  // =============================================

  const canvasListViewColumnSettingsEnabled = computed(() => {
    return get(
      clientConfig,
      'features.scenario.assortmentCanvas.canvasListViewColumnSettings',
      false
    );
  });

  const cannGroupColumnSettingsEnabled = computed(() => {
    return get(clientConfig, 'features.scenario.assortmentCanvas.cannGroupColumnSettings', false);
  });

  const cannGroupCollapseEnabled = computed(() => {
    return get(features, 'scenario.assortmentCanvas.cannGroupCollapseEnabled', false);
  });

  const finalCheckpointLockEnabled = computed(() => {
    return get(features, 'scenario.assortmentCanvas.finalCheckpointLockEnabled', false);
  });

  const microspaceOptimisationEnabled = computed(() => {
    return get(features, 'scenario.assortmentCanvas.microspaceOptimisationEnabled', false);
  });

  const canvasFilterLegendEnabled = computed(() => {
    return get(clientConfig, 'features.canvasFilterLegendEnabled', false);
  });

  const useWorkpackageFillForOptimiserDefault = computed(() => {
    return get(clientConfig, 'features.useWorkpackageFillForOptimiserDefault', false);
  });

  const showInactiveProductsOnCanvas = computed(() => {
    return get(clientConfig, 'canvas.showInactiveProducts', true);
  });

  // =============================================
  // Reporting
  // =============================================

  const showLiveCanvasInReportingMenu = computed(() => {
    return get(clientConfig, 'features.showLiveCanvasInReportingMenu', false);
  });

  const showLayouts = computed(() => {
    return get(clientConfig, 'features.showLayouts', false);
  });

  // =============================================
  // Extracts
  // =============================================

  const availableExtractsExports = computed(() => {
    return get(clientConfig, 'features.availableExtractsExports', []);
  });

  const ftpSpaceTestEnabled = computed(() => {
    return get(clientConfig, 'features.extracts.ftpSpaceTestEnabled', false);
  });

  const publishToDataPlatform = computed(() => {
    return get(features, 'extracts.publishToDataPlatform', false);
  });

  // =============================================
  // Clustering
  // =============================================
  const hasClusteringFromDataEnabled = computed(() => {
    return get(clientConfig, 'features.clusteringFromDataEnabled', false);
  });

  const aiClusteringAnalysisEnabled = computed(() => {
    return get(features, 'ai.aiDataInsights.aiClusteringAnalysisEnabled', false);
  });

  const aiCdtAnalysisEnabled = computed(() => {
    return get(features, 'ai.aiDataInsights.aiCdtAnalysisEnabled', false);
  });

  const aiPanelEnabled = computed(() => {
    return get(features, 'ai.aiPanelEnabled', false);
  });

  const aiAssistantEnabled = computed(() => {
    return get(features, 'ai.aiAssistantEnabled', false);
  });

  const aiAgentModeEnabled = computed(() => {
    return get(features, 'ai.aiAgentModeEnabled', false);
  });

  /**
   * Returns true if full AI agent functionality (tools + page context) is enabled for the
   * given section key. Sections absent from the agentEnabledSections allowlist in features.yaml
   * fall back to general assistant mode only.
   * @param {string|null|undefined} section - The section key (e.g. 'attribute-editor')
   * @returns {boolean}
   */
  function isAgentEnabledForSection(section) {
    if (!section) return false;
    const enabledSections = get(features, 'ai.agentEnabledSections', []);
    return Array.isArray(enabledSections) && enabledSections.includes(section);
  }

  // =============================================
  // Product Switching Service
  // =============================================

  const productSwitchingServiceEnabled = computed(() => {
    return get(features, 'switching.productSwitchingServiceEnabled', false);
  });

  const usePollingForUpdates = computed(() => {
    return get(features, 'switching.usePollingForUpdates', false);
  });

  // =============================================
  // Other
  // =============================================

  const qaControlsEnabled = computed(() => {
    return get(clientConfig, 'features.qaControlsEnabled', false);
  });

  const hasSeasonalityEnabled = computed(() => {
    return get(clientConfig, 'features.seasonalityEnabled', false);
  });

  const hasSpacebreakFromDataEnabled = computed(() => {
    return get(clientConfig, 'features.spacebreaksFromDataEnabled', false);
  });

  const hasRegionsByAttributeEnabled = computed(() => {
    return get(clientConfig, 'features.regionsByAttributeEnabled', false);
  });

  const showStrictlyNecessaryCookiesNotice = computed(() => {
    // Embedded, the shell is the single cookie-consent authority and loads the one Osano instance
    // for the shared document. Forcing this off gates both AO's Osano injection (App.vue) and the
    // footer's cookie controls (MainFooter.vue).
    if (isEmbedded()) return false;
    return get(clientConfig, 'features.showStrictlyNecessaryCookiesNotice', true);
  });

  const showNotImplemented = computed(() => {
    return get(clientConfig, 'features.showNotImplemented', true);
  });

  const isMarginWithoutFundingEnabled = computed(() => {
    return get(features, 'workpackages.isMarginWithoutFundingEnabled', false);
  });

  const customerSegmentsEnabled = computed(() => {
    return get(clientConfig, 'features.customerSegmentsEnabled', false);
  });

  const hasPeriodIdsEnabled = computed(() => {
    return get(clientConfig, 'features.workpackagePeriodIdsEnabled', false);
  });

  const calculateTrendEnabled = computed(() => {
    return get(clientConfig, 'features.calculateTrendEnabled', false);
  });

  const hasAttributeImportFromPlanogramEnabled = computed(() => {
    return get(
      clientConfig,
      'features.scenario.products.isImportAttributesFromPlanogramEnabled',
      false
    );
  });

  const hasAttributeImportFromScenarioEnabled = computed(() => {
    return get(clientConfig, 'features.importAttributesFromScenarioEnabled', false);
  });

  const fullToolGuideEnabled = computed(() => {
    return get(clientConfig, 'features.fullToolGuideEnabled', false);
  });

  // =============================================
  // Beta feature toggles
  // =============================================

  const betaHasMandatoryPeriodIdsEnabled = computed(() => {
    return get(clientConfig, 'betaFeatures.rcMandatoryPeriodIdsEnabled', false);
  });

  const betaTemplateDeleteEnabled = computed(() => {
    return get(clientConfig, 'betaFeatures.templateDeleteEnabled', false);
  });

  const betaIsExtractDeleteEnabled = computed(() => {
    return get(clientConfig, 'betaFeatures.extractDeleteEnabled', false);
  });

  const betaClientIDEditEnabled = computed(() => {
    return get(clientConfig, 'betaFeatures.clientIDEditEnabled', false);
  });

  const betaHasDependencyTreeOnfurnitureEditorEnabled = computed(() => {
    return get(clientConfig, 'betaFeatures.dependencyTreeOnFurnitureEditorEnabled', false);
  });

  const betaAutoGenerateWorkpackagePeriodDatesEnabled = computed(() => {
    return get(clientConfig, 'betaFeatures.autoGenerateWorkpackagePeriodDates', false);
  });

  const betaHideProductsNotListedToAssortment = computed(() => {
    return get(clientConfig, 'features.rcHideProductsNotListedToAssortment', false);
  });

  const enableFinalCheckpoints = computed(() => {
    return get(clientConfig, 'betaFeatures.enableFinalCheckpoints', false);
  });

  const isChristmasThemeEnabled = computed(() => {
    return get(features, 'christmasThemeEnabled', false);
  });

  return {
    clientConfig,

    // Bundles and templates
    bundlesEnabled,
    templatesEnabled,

    // Workpackages
    freshEnabled,
    gridContextMenusEnabled,
    hideAnalysisOnlySelection,
    historicKPIsEnabled,
    performancePeriodPreSetSelectMenuEnabled,
    performancePeriodPreSetSelectOptions,
    previousWorkpackageLinkingEnabled,
    setByEndDateDefault,
    showSetByEndDateToggle,
    syntheticFurnitureEnabled,
    defaultFillAccordingTo,

    // Products
    hasProductsExistingFromNewInTemplateEnabled,
    hasProductLineEnabled,
    hasKPKUEnabled,
    hasPalletContentEnabled,
    autoReAddRecentlySoldProductsWhenNotChild,
    dependentProductsEnabled,

    // Product Modelling
    isProductModellingToggleProductViewsEnabled,
    productModellingViewsToggleType,
    isCopyModellingDecisionsEnabled,
    isTotalSalesOverrideEnabled,
    isVolumeRoSEnabled,
    showProductModellingForNewProductsOnly,
    hasClientProductKeyColumnEnabled,
    useMarkdownAndWasteToCalculateMarginAndPriceEnabled,
    productModellingAlertsCriteria,
    modellingMainPageAllowedColumns,
    modellingDetailPageAllowedColumns,
    defaultModellingViewToAllProducts,
    hideWasteAndMarkdownColumns,

    // Assortment Canvas
    canvasListViewColumnSettingsEnabled,
    cannGroupColumnSettingsEnabled,
    cannGroupCollapseEnabled,
    finalCheckpointLockEnabled,
    microspaceOptimisationEnabled,
    canvasFilterLegendEnabled,
    useWorkpackageFillForOptimiserDefault,
    showInactiveProductsOnCanvas,

    // Reporting
    showLiveCanvasInReportingMenu,
    showLayouts,

    // Extracts
    availableExtractsExports,
    ftpSpaceTestEnabled,
    publishToDataPlatform,

    // Clustering
    hasClusteringFromDataEnabled,
    aiClusteringAnalysisEnabled,
    aiPanelEnabled,
    aiCdtAnalysisEnabled,
    aiAssistantEnabled,
    aiAgentModeEnabled,
    isAgentEnabledForSection,

    // Product Switching Service
    productSwitchingServiceEnabled,
    usePollingForUpdates,

    // Other
    qaControlsEnabled,
    hasSeasonalityEnabled,
    hasSpacebreakFromDataEnabled,
    hasRegionsByAttributeEnabled,
    showStrictlyNecessaryCookiesNotice,
    showNotImplemented,
    isMarginWithoutFundingEnabled,
    customerSegmentsEnabled,
    hasPeriodIdsEnabled,
    calculateTrendEnabled,
    betaAutoGenerateWorkpackagePeriodDatesEnabled,
    hasAttributeImportFromPlanogramEnabled,
    hasAttributeImportFromScenarioEnabled,
    fullToolGuideEnabled,

    // Beta feature toggles - should be removed once feature is enabled globally
    betaHasMandatoryPeriodIdsEnabled,
    betaTemplateDeleteEnabled,
    betaIsExtractDeleteEnabled,
    betaClientIDEditEnabled,
    betaHasDependencyTreeOnfurnitureEditorEnabled,
    betaHideProductsNotListedToAssortment,
    enableFinalCheckpoints,

    isChristmasThemeEnabled,
  };
}
